import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useRouter } from 'next/router';
import { useTranslation } from 'next-i18next';
import { QUERY_KEYS } from '@restworld/utils-common';
import { useJobPositionsService } from '@restworld/data-services';
import { showToast } from '@restworld/ui-ds-base';

export function useDuplicateJobPosition() {
  const queryClient = useQueryClient();
  const jobPositionsService = useJobPositionsService();
  const router = useRouter();
  const { t } = useTranslation('job-positions-page');

  const { mutate: duplicateJobPosition, isPending: isDuplicating } = useMutation({
    mutationFn: ({ jobPositionId }: { jobPositionId: string }) =>
      jobPositionsService.duplicate({
        jobPositionId,
        params: {
          status_key: 'jp_draft',
        },
      }),
    onSuccess: (response) => {
      // Invalidate the job positions list query to refresh the data
      queryClient.invalidateQueries({
        queryKey: [QUERY_KEYS.JOB_POSITIONS.LIST],
      });

      // Land the duplicated draft on the REVIEW step (the default). The review step's own
      // pay-transparency guard (D.Lgs. 96/2026) then bounces the employer to the salary step
      // ONLY if a salary estimate is missing on the duplicated JP (e.g. it was duplicated from
      // a legacy net offer that carries no estimates); otherwise they stay on review.
      const newJpId = response?.data?.id;
      if (newJpId) {
        router.push(`/employer/job-positions/onboarding?job_position_id=${newJpId}&step=review`);
      }
    },
    onError: () => {
      showToast(t('jp_duplicate_error'), 'error');
    },
  });

  return { duplicateJobPosition, isDuplicating };
}
